import { MerchantPlatform } from "./merchantBranding";

export type GetStartedPanel =
  | "dashboard"
  | "orders"
  | "notifications"
  | "settings"
  | "help";

export interface GetStartedTip {
  id: string;
  title: string;
  subtitle: string;
  body: string[];
  highlights: string[];
  panel?: GetStartedPanel;
  ctaLabel?: string;
}

const STORAGE_PREFIX = "zulu.merchant.getStarted.v1:";

export function getStartedStorageKey(
  platform: MerchantPlatform,
  shop: string
): string {
  const shopKey = String(shop || "default")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "_")
    .slice(0, 80);
  return `${STORAGE_PREFIX}${platform}:${shopKey || "default"}`;
}

export function hasCompletedGetStarted(
  platform: MerchantPlatform,
  shop: string
): boolean {
  try {
    return localStorage.getItem(getStartedStorageKey(platform, shop)) === "done";
  } catch {
    return false;
  }
}

export function markGetStartedComplete(
  platform: MerchantPlatform,
  shop: string
): void {
  try {
    localStorage.setItem(getStartedStorageKey(platform, shop), "done");
  } catch {
    // ignore quota / private mode
  }
}

export function resetGetStarted(
  platform: MerchantPlatform,
  shop: string
): void {
  try {
    localStorage.removeItem(getStartedStorageKey(platform, shop));
  } catch {
    // ignore
  }
}

function platformIntro(platform: MerchantPlatform): GetStartedTip {
  if (platform === "shopify") {
    return {
      id: "welcome",
      title: "Welcome to your Zulu merchant portal",
      subtitle: "Shopify store connected",
      body: [
        "This is your home for Zulu deliveries from Shopify checkout through drop-off.",
        "Take a quick tour of Dashboard, Orders, Notifications, Settings, and Help — then you are ready to go live.",
      ],
      highlights: [
        "Rates appear at Shopify checkout",
        "Paid Zulu orders import automatically",
        "Print labels and track every delivery here",
      ],
    };
  }
  if (platform === "custom") {
    return {
      id: "welcome",
      title: "Welcome to your Zulu merchant portal",
      subtitle: "Custom store connected",
      body: [
        "Manage deliveries for your own shop from this portal.",
        "Use your integration API key for rates and paid-order webhooks, then track everything here.",
      ],
      highlights: [
        "Keep your API key private on your backend",
        "Send paid orders to Zulu after checkout",
        "Track status, labels, and alerts in one place",
      ],
    };
  }
  if (platform === "woocommerce") {
    return {
      id: "welcome",
      title: "Welcome to your Zulu merchant portal",
      subtitle: "WooCommerce store connected",
      body: [
        "Track Zulu deliveries from your WooCommerce store in real time.",
        "This short tour shows where to manage orders, labels, alerts, and settings.",
      ],
      highlights: [
        "Orders sync after payment",
        "Labels and status live in Orders",
        "Settings control pickup and preferences",
      ],
    };
  }
  return {
    id: "welcome",
    title: "Welcome to your Zulu merchant portal",
    subtitle: "Get oriented in under a minute",
    body: [
      "This tour walks through the tools you will use every day to run Zulu delivery.",
    ],
    highlights: ["Dashboard", "Orders", "Settings & Help"],
  };
}

function platformIntegrationTip(platform: MerchantPlatform): GetStartedTip {
  if (platform === "shopify") {
    return {
      id: "platform",
      title: "Shopify checkout & import",
      subtitle: "How orders reach Zulu",
      panel: "help",
      body: [
        "When a buyer chooses Zulu at Shopify checkout and pays, the order imports into your portal.",
        "If rates are missing at checkout, check Dashboard connection/pricing status and Settings → Pricing.",
      ],
      highlights: [
        "Keep the Zulu app installed on the correct shop",
        "Use a covered postal code when testing rates",
        "Open Help for Shopify install and rate guides",
      ],
      ctaLabel: "Open Help",
    };
  }
  if (platform === "custom") {
    return {
      id: "platform",
      title: "Custom API integration",
      subtitle: "Rates and paid orders",
      panel: "help",
      body: [
        "Your storefront backend calls Zulu with the API key for delivery rates, then posts paid orders after payment.",
        "The raw API key is shown only once after store setup — store it in secrets, never in public frontend code.",
      ],
      highlights: [
        "Bearer token on rates and webhook calls",
        "Include accurate pickup and dropoff addresses",
        "Help has step-by-step custom API guides",
      ],
      ctaLabel: "Open Help",
    };
  }
  return {
    id: "platform",
    title: "Platform connection",
    subtitle: "Keep your store linked",
    panel: "help",
    body: [
      "Your ecommerce platform sends paid Zulu deliveries into this portal.",
      "Use Help anytime for connection and troubleshooting steps.",
    ],
    highlights: ["Connection status on Dashboard", "Help for platform guides"],
    ctaLabel: "Open Help",
  };
}

export function getStartedTips(platform: MerchantPlatform): GetStartedTip[] {
  return [
    platformIntro(platform),
    {
      id: "dashboard",
      title: "Dashboard at a glance",
      subtitle: "Live delivery snapshot",
      panel: "dashboard",
      body: [
        "See in-transit, delivered, and total order counts for the date range you pick.",
        "Store connection and checkout pricing status tell you if the store is healthy before you take orders.",
      ],
      highlights: [
        "Date filter for today, week, month, or custom",
        "Recent orders open full details",
        "Refresh when you expect a new import",
      ],
      ctaLabel: "Go to Dashboard",
    },
    {
      id: "orders",
      title: "Orders & shipping labels",
      subtitle: "Manage every delivery",
      panel: "orders",
      body: [
        "Filter by status and date, search, and open any order for addresses and progress.",
        "Download or print shipping labels so drivers can pick up and deliver correctly.",
      ],
      highlights: [
        "In transit, delivered, and all views",
        "Order detail sheet with full address",
        "Label size defaults live in Settings",
      ],
      ctaLabel: "Go to Orders",
    },
    {
      id: "notifications",
      title: "Notifications",
      subtitle: "Never miss a status change",
      panel: "notifications",
      body: [
        "Unread delivery updates appear here with a badge on the sidebar.",
        "Jump from an alert into Orders when you need to act.",
      ],
      highlights: [
        "Badge shows unread count",
        "Configure which events you get in Settings",
      ],
      ctaLabel: "Go to Notifications",
    },
    {
      id: "settings",
      title: "Settings that keep you running",
      subtitle: "Profile, pricing, labels, alerts",
      panel: "settings",
      body: [
        "Update store profile and default pickup so drivers know where to collect packages.",
        "Review pricing services, choose label formats, and tune notification preferences.",
      ],
      highlights: [
        "Profile & pickup address",
        "Pricing / delivery services",
        "Label formats & notification rules",
      ],
      ctaLabel: "Go to Settings",
    },
    platformIntegrationTip(platform),
    {
      id: "done",
      title: "You’re ready",
      subtitle: "Start managing deliveries",
      panel: "dashboard",
      body: [
        "You can reopen this tour anytime from Help → Get started tips.",
        "When you are set, head to the Dashboard and watch new Zulu orders come in.",
      ],
      highlights: [
        "Help center for deeper guides",
        "Email support if something looks off",
      ],
      ctaLabel: "Open Dashboard",
    },
  ];
}
